<template>
    <div class="ngdialog-content">
        <dialog-header>
            <h4 v-if="editable">{{ $t('organizations.permissions') }}</h4>
            <h4 v-else>{{ $t('organizations.edit_permissions') }}</h4>
        </dialog-header>

        <form name="permissionsForm" novalidate @submit.prevent="submitHandler" class="form">
            <div class="ngdialog__body">
                <fieldset class="form__group">
                    <div class="row form__row">
                        <label class="form__label">{{ $t('fields.organization_parameter') }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.parameter"
                            :disabled="!editable"
                        >
                            <option :value="null" :label="getInheritedLabel('parameter')"></option>
                            <option
                                v-if="availableLayerOptions.includes('information')"
                                value="information"
                                :label="$t('organizations.parameter_informationlayer')"
                                >{{ $t('organizations.parameter_informationlayer') }}</option
                            >
                            <option
                                v-if="availableLayerOptions.includes('operator')"
                                value="operator"
                                :label="$t('organizations.parameter_operatorlayer')"
                                >{{ $t('organizations.parameter_operatorlayer') }}</option
                            >
                            <option
                                v-if="availableLayerOptions.includes('service')"
                                value="service"
                                :label="$t('organizations.parameter_servicelayer')"
                                >{{ $t('organizations.parameter_servicelayer') }}</option
                            >
                            <option
                                v-if="availableLayerOptions.includes('oem')"
                                value="oem"
                                :label="$t('organizations.parameter_oemlayer')"
                                >{{ $t('organizations.parameter_oemlayer') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable"
                                type="checkbox"
                                v-model="formData.parameter_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('label_admin')">
                        <label class="form__label">{{
                            $t('fields.organization_label_admin')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.label_admin"
                            :disabled="!editable"
                        >
                            <option :value="null" :label="getInheritedLabel('label_admin')">{{
                                getInheritedLabel('label_admin')
                            }}</option>
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.label_admin && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.label_admin === false"
                                type="checkbox"
                                v-model="formData.label_admin_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('label_assign')">
                        <label class="form__label">{{
                            $t('fields.organization_label_assign')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.label_assign"
                            :disabled="!editable"
                        >
                            <option :value="null" :label="getInheritedLabel('label_assign')">{{
                                getInheritedLabel('label_assign')
                            }}</option>
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.label_assign && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.label_assign === false"
                                type="checkbox"
                                v-model="formData.label_assign_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('notification_admin')">
                        <label class="form__label">{{
                            $t('fields.organization_label_notification_admin')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.notification_admin"
                            :disabled="!editable"
                        >
                            <option
                                :value="null"
                                :label="getInheritedLabel('notification_admin')"
                                >{{ getInheritedLabel('notification_admin') }}</option
                            >
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.notification_admin && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.notification_admin === false"
                                type="checkbox"
                                v-model="formData.notification_admin_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('system_activate')">
                        <label class="form__label">{{
                            $t('fields.organization_label_system_activate')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.system_activate"
                            :disabled="!editable"
                        >
                            <option :value="null" :label="getInheritedLabel('system_activate')">{{
                                getInheritedLabel('system_activate')
                            }}</option>
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.system_activate && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.system_activate === false"
                                type="checkbox"
                                v-model="formData.system_activate_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('system_register')">
                        <label class="form__label">{{
                            $t('fields.organization_label_system_register')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.system_register"
                            :disabled="!editable"
                        >
                            <option :value="null" :label="getInheritedLabel('system_register')">{{
                                getInheritedLabel('system_register')
                            }}</option>
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.system_register && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.system_register === false"
                                type="checkbox"
                                v-model="formData.system_register_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('webserver_settings')">
                        <label class="form__label">{{
                            $t('fields.organization_label_webserver_settings')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.webserver_settings"
                            :disabled="!editable"
                        >
                            <option
                                :value="null"
                                :label="getInheritedLabel('webserver_settings')"
                                >{{ getInheritedLabel('webserver_settings') }}</option
                            >
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.webserver_settings && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.webserver_settings === false"
                                type="checkbox"
                                v-model="formData.webserver_settings_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('webserver_update')">
                        <label class="form__label">{{
                            $t('fields.organization_label_webserver_update')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.webserver_update"
                            :disabled="!editable"
                        >
                            <option :value="null" :label="getInheritedLabel('webserver_update')">{{
                                getInheritedLabel('webserver_update')
                            }}</option>
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.webserver_update && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.webserver_update === false"
                                type="checkbox"
                                v-model="formData.webserver_update_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                    <div class="row form__row" v-if="isFieldVisible('webserver_recorder')">
                        <label class="form__label">{{
                            $t('fields.organization_label_webserver_recorder')
                        }}</label>
                        <select
                            class="form__field form__field--split"
                            v-model="formData.webserver_recorder"
                            :disabled="!editable"
                        >
                            <option
                                :value="null"
                                :label="getInheritedLabel('webserver_recorder')"
                                >{{ getInheritedLabel('webserver_recorder') }}</option
                            >
                            <option :value="false" :label="$t('common.no')">{{
                                $t('common.no')
                            }}</option>
                            <option
                                v-if="formData.webserver_recorder && !editable"
                                :value="true"
                                :label="$t('common.yes')"
                                >{{ $t('common.yes') }}</option
                            >
                        </select>
                        <label
                            class="form__field form__field--split form__field--checkbox no-border"
                        >
                            <input
                                :disabled="!editable || formData.webserver_recorder === false"
                                type="checkbox"
                                v-model="formData.webserver_recorder_inheritable"
                            />
                            <span>{{ $t('general.inheritable') }}</span>
                        </label>
                    </div>
                </fieldset>
            </div>

            <dialog-buttons
                v-if="editable"
                :submit-text="$t('buttons.save_changes')"
                :disabled="loading"
            />
        </form>
    </div>
</template>

<script>
import * as api from '@/api';
import DialogHeader from '@/components/DialogHeader';
import DialogButtons from '@/components/DialogButtons';
import { mapState, mapGetters } from 'vuex';

export default {
    components: {
        DialogHeader,
        DialogButtons,
    },
    props: {
        organizationId: {
            type: [Boolean, String],
            default: false,
        },
        permissions: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            loading: false,
            formData: {
                parameter: '',
                label_admin: 0,
                label_assign: 0,
                notification_admin: 0,
                system_activate: 0,
                system_register: 0,
                webserver_settings: 0,
                webserver_update: 0,
                webserver_recorder: 0,
                webserver_update_inheritable: false,
                webserver_settings_inheritable: false,
                webserver_recorder_inheritable: false,
                system_register_inheritable: false,
                system_activate_inheritable: false,
                parameter_inheritable: false,
                notification_admin_inheritable: false,
                label_assign_inheritable: false,
                label_admin_inheritable: false,
            },
            layerHierarchy: {
                information: 0,
                operator: 1,
                service: 2,
                oem: 3,
            },
        };
    },
    computed: {
        ...mapState(['activeOrganization']),
        ...mapGetters(['userOrganizationId', 'isParentOrganization']),
        availableLayerOptions() {
            const layerOptions = ['information', 'operator', 'service', 'oem'];
            let availableOptions;
            if (this.permissions.inherited_parameter !== null) {
                const index = layerOptions.indexOf(this.permissions.inherited_parameter);
                availableOptions = layerOptions.slice(0, index);
            } else {
                availableOptions = layerOptions;
            }
            if (!availableOptions.includes(this.formData.parameter))
                availableOptions.push(this.formData.parameter);
            return availableOptions;
        },
        parentOrganization() {
            return this.isParentOrganization(this.activeOrganization.breadcrumbs);
        },
        editable() {
            const canEdit = this.$can('edit', 'Organization permissions');
            return canEdit && this.parentOrganization;
        },
    },
    mounted() {
        this.prepareData();
    },
    methods: {
        prepareData() {
            console.log(this.permissions);
            for (const key in this.formData) {
                let value = this.permissions[key];
                this.formData[key] = value;
            }
        },
        submitHandler() {
            this.savePermissions();
        },
        isFieldVisible(field) {
            if (this.parentOrganization || this.editable) {
                return this.permissions[`inherited_${field}`] !== false;
            }
            return (
                this.permissions[`inherited_${field}`] !== false &&
                this.permissions[field] !== false
            );
        },
        getInheritedLabel(parameterName) {
            if (parameterName === 'parameter') {
                return (
                    this.$t(
                        `organizations.parameter_${this.permissions.inherited_parameter}layer`
                    ) +
                    ' / ' +
                    this.$t('common.inherited')
                );
            } else {
                const valueText = this.permissions['inherited_' + parameterName]
                    ? this.$t('common.yes')
                    : this.$t('common.no');
                return valueText + ' / ' + this.$t('common.inherited');
            }
        },
        async savePermissions() {
            const data = {
                parameter: this.formData.parameter,
                label_admin: this.formData.label_admin,
                label_assign: this.formData.label_assign,
                notification_admin: this.formData.notification_admin,
                system_activate: this.formData.system_activate,
                system_register: this.formData.system_register,
                webserver_settings: this.formData.webserver_settings,
                webserver_update: this.formData.webserver_update,
                webserver_recorder: this.formData.webserver_recorder,
                parameter_inheritable: this.formData.parameter_inheritable,
                webserver_update_inheritable:
                    this.formData.webserver_update !== false
                        ? this.formData.webserver_update_inheritable
                        : false,
                webserver_settings_inheritable:
                    this.formData.webserver_settings !== false
                        ? this.formData.webserver_settings_inheritable
                        : false,
                webserver_recorder_inheritable:
                    this.formData.webserver_recorder !== false
                        ? this.formData.webserver_recorder_inheritable
                        : false,
                system_register_inheritable:
                    this.formData.system_register !== false
                        ? this.formData.system_register_inheritable
                        : false,
                system_activate_inheritable:
                    this.formData.system_activate !== false
                        ? this.formData.system_activate_inheritable
                        : false,
                notification_admin_inheritable:
                    this.formData.notification_admin !== false
                        ? this.formData.notification_admin_inheritable
                        : false,
                label_assign_inheritable:
                    this.formData.label_assign !== false
                        ? this.formData.label_assign_inheritable
                        : false,
                label_admin_inheritable:
                    this.formData.label_admin !== false
                        ? this.formData.label_admin_inheritable
                        : false,
            };

            try {
                await api.organizations.updatePermissions(this.organizationId, data);
                this.loading = false;
                this.$emit('close');
            } catch (error) {
                console.error(error);
                this.loading = false;
                this.$notify({
                    type: 'error',
                    text: this.$t('errors.organization_permission_update_error'),
                });
            }
        },
    },
};
</script>
